import { parseOAuthResult, type OAuthConnectResult } from './oauthResult';

/**
 * localStorage key the connect tab writes its result to. This is the single
 * source of truth for the hand-off: whoever reads it removes it, so the several
 * things that can wake the app tab (broadcast / storage event / focus / mount)
 * can't act on the same connect twice.
 */
export const OAUTH_RESULT_STORAGE_KEY = 'social-oauth:result';

/**
 * Same-origin channel the connect tab pings once it has stored its result, so
 * the app tab reacts immediately instead of waiting to notice the tab close.
 */
export const SOCIAL_OAUTH_CHANNEL = 'social-oauth';

/** Discriminator on the broadcast, so an unrelated message can't wake us. */
export const SOCIAL_OAUTH_NUDGE = 'social-oauth:result';

/**
 * Open the tab that will carry the OAuth flow.
 *
 * Two things here are load-bearing:
 *
 * `_blank` — never a name. `window.open` navigates any existing window already
 * carrying the name it is given, which is why the old fixed `oauth_popup` name
 * made "Connect X" hijack the LinkedIn window (LIL-490). `_blank` always
 * creates a new browsing context, so no two connects can collide.
 *
 * Called BEFORE awaiting the backend for the auth URL, and pointed at the
 * provider afterwards with `sendConnectTab`. Browsers only allow `window.open`
 * while a user gesture is still being handled; opening it after an `await`
 * gets it blocked.
 */
export function openConnectTab(): Window | null {
  return window.open('', '_blank');
}

/** Send an already-open connect tab to the provider's authorization page. */
export function sendConnectTab(tab: Window, authUrl: string): void {
  tab.location.href = authUrl;
}

/**
 * Run in the connect tab when it lands back on our origin: hand the result to
 * the app tab and close.
 *
 * Whether this document IS a connect tab is decided by the OAuth result params
 * the provider put on the URL, plus having an opener. Deliberately NOT by
 * `window.name` — browsers clear that on cross-site navigation, so the old
 * check silently failed after the OAuth round trip and the whole app booted
 * inside the popup.
 *
 * Returns true when it took over, in which case the caller must not mount the
 * app. False means "this is an ordinary load, carry on" — including the case
 * where there is no opener, so this tab has to surface the result itself.
 */
export function relayOAuthResultAndClose(search: string = window.location.search): boolean {
  const result = parseOAuthResult(search);
  if (!result) return false;
  if (!window.opener) return false;

  try {
    localStorage.setItem(OAUTH_RESULT_STORAGE_KEY, JSON.stringify(result));
  } catch {
    // Non-fatal: the app tab still reconciles when it sees this tab close.
  }

  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(SOCIAL_OAUTH_CHANNEL);
      channel.postMessage({ type: SOCIAL_OAUTH_NUDGE });
      channel.close();
    }
  } catch {
    // Non-fatal: the stored result and the tab close still deliver.
  }

  window.close();
  return true;
}

/**
 * Read and clear the relayed result in the app tab. The remove is what dedupes
 * the wake-ups against each other — the first to call this gets the result and
 * the rest get null.
 */
export function consumeStoredOAuthResult(): OAuthConnectResult | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(OAUTH_RESULT_STORAGE_KEY);
    if (!raw) return null;
    localStorage.removeItem(OAUTH_RESULT_STORAGE_KEY);
  } catch {
    return null;
  }

  try {
    return JSON.parse(raw) as OAuthConnectResult;
  } catch {
    // Corrupted value, already cleared above so it can't wedge the next connect.
    return null;
  }
}
